import React, { useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";

// Poll body — the same card in three states (design 1d):
//   NOT VOTED  → option buttons
//   VOTED/OPEN → result bars + %/total, own choice marked, "Change vote"
//   CLOSED     → final aggregate bars, muted, "Poll closed" note, no voting
// Results are ALWAYS aggregate-only (bars/%/total) — never identities.
//
// Emphasis rules per state (design 1d, intentional shift): open results bold
// the member's OWN row (800); closed results bold the LEADER (700) while the
// own row keeps its blue-ish fill and a desaturated "✓ Your vote" tag.
//
// Controlled: `poll` = { closes_at, is_closed, total_votes, my_vote_option_id,
// options:[{id,text,votes,percentage}] }. `onVote(optionId)` casts/switches
// (parent owns the API call + optimistic update). `busy` disables input.

function closesLabel(closesAt, isClosed) {
  if (!closesAt) return null;
  const d = new Date(closesAt);
  if (Number.isNaN(d.getTime())) return null;
  if (isClosed) {
    return `Poll closed on ${d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })}`;
  }
  return `Closes in ${formatDistanceToNowStrict(d)}`;
}

const OptionButton = ({ option, selected, disabled, onPick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onPick(option.id)}
    className={[
      "group flex items-center gap-3 w-full text-left px-3.5 py-3 rounded-xl border transition",
      "disabled:opacity-60 disabled:cursor-default",
      selected
        ? "border-[#9CC6FF] bg-[#F4F9FF]"
        : "border-[#E4E9F0] bg-white hover:border-[#8FBEFC] hover:bg-[#F7FAFF]",
    ].join(" ")}
  >
    {/* Radio S16 (unselected) / S17 (selected — radial-gradient fill) */}
    <span
      className={[
        "w-[18px] h-[18px] rounded-full border-2 box-border flex-none",
        selected ? "border-[#2283FF]" : "border-[#C3CEDC] bg-white",
      ].join(" ")}
      style={
        selected
          ? { background: "radial-gradient(circle, #2283FF 0px, #2283FF 4.5px, #FFFFFF 5px)" }
          : undefined
      }
    />
    <span className="text-[14.5px] font-semibold text-[#22334B]">{option.text}</span>
  </button>
);

const ResultBar = ({ option, mine, closed, leader }) => {
  const pct = Math.max(0, Math.min(100, Math.round(option.percentage ?? 0)));
  const fill = closed
    ? mine
      ? "#DCE9FA"
      : "#E6ECF3"
    : mine
      ? "#CBE3FF"
      : "#EAEFF5";
  const border = closed ? "#E7EBF1" : mine ? "#BFDBFF" : "#E7EBF1";
  const textColor = closed ? "#3E5069" : "#22334B";
  // Open: own row 800; closed: leading row 700, everything else 600.
  const weight = closed ? (leader ? 700 : 600) : mine ? 800 : 600;
  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ background: closed ? "#FCFDFE" : "#FDFEFF", border: `1px solid ${border}` }}
    >
      <div
        className="absolute inset-y-0 left-0"
        style={{ width: `${pct}%`, background: fill }}
      />
      <div className="relative flex items-center gap-2 min-h-[18px] px-[13px] py-[13px] sm:px-3.5 sm:py-[11px]">
        <span
          className="text-[14.5px]"
          style={{ color: textColor, fontWeight: weight }}
        >
          {option.text}
        </span>
        {mine && (
          <span
            className="text-[11.5px] sm:text-[12px] font-bold"
            style={{ color: closed ? "#7595C8" : "#1668D9" }}
          >
            <span className="sm:hidden">✓ Yours</span>
            <span className="hidden sm:inline">✓ Your vote</span>
          </span>
        )}
        <span
          className="ml-auto text-[13px] font-bold tabular-nums"
          style={{ color: textColor }}
        >
          {pct}%
        </span>
      </div>
    </div>
  );
};

const PollCard = ({ poll, onVote, busy }) => {
  const [editing, setEditing] = useState(false);
  if (!poll) return null;

  const closed = Boolean(poll.is_closed);
  const voted = poll.my_vote_option_id != null;
  const showOptions = !closed && (!voted || editing);
  const options = poll.options || [];
  const total = poll.total_votes ?? 0;
  const closeText = closesLabel(poll.closes_at, closed);

  // Closed-state leader (highest share) — emphasized per design 1d state 3.
  const maxShare = options.length
    ? Math.max(...options.map((o) => o.votes ?? o.percentage ?? 0))
    : 0;
  const isLeader = (o) => (o.votes ?? o.percentage ?? 0) === maxShare && maxShare > 0;

  const handlePick = (optionId) => {
    setEditing(false);
    onVote(optionId);
  };

  const meta = [
    `${total} ${total === 1 ? "vote" : "votes"}${!voted && !closed ? " so far" : ""}`,
    closeText,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="mt-4">
      {showOptions ? (
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <OptionButton
              key={o.id}
              option={o}
              selected={poll.my_vote_option_id === o.id}
              disabled={busy}
              onPick={handlePick}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <ResultBar
              key={o.id}
              option={o}
              mine={poll.my_vote_option_id === o.id}
              closed={closed}
              leader={isLeader(o)}
            />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-[13px] sm:mt-3.5 gap-3">
        {/* Closed meta drops the "voting is locked" suffix on mobile (1f). */}
        <span className="text-[12.5px] font-medium text-[#93A1B4]">
          {closed ? (
            <>
              <span className="hidden sm:inline">{`${meta} · voting is locked`}</span>
              <span className="sm:hidden">{meta}</span>
            </>
          ) : (
            meta
          )}
        </span>
        {!closed && voted && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center h-9 px-1 text-[13px] sm:h-auto sm:px-0 sm:text-[12.5px] font-bold text-[#2283FF] hover:text-[#1668D9]"
          >
            Change vote
          </button>
        )}
      </div>
    </div>
  );
};

export default PollCard;
